<template>
  <div>
    <b-row v-if="!isLoading && !isFormInvalid">
      <b-col>
        <b-table
          v-if="meetingResolutionsWithParents && meetingResolutionsWithParents.length > 0"
          ref="voting-table"
          class="agv-table"
          hover
          bordered
          :items="meetingResolutionsWithParents"
          :fields="fields"
          :tbody-tr-class="detailsClassFn"
        >
          <template #head(Pour)>
            <span>Pour</span>
            <br />
            <b-button
              size="sm"
              class="text-nowrap"
              variant="light"
              :disabled="currentMandate.voted_by_correspondence == correspondenceTypes.by_app"
              @click="setAll(voteTypes.for)"
            >
              {{ $t("general.Tous") }}
              <font-awesome-icon
                icon="check-square"
                :class="{ 'color-green': isChecked(voteTypes.for) }"
              />
            </b-button>
          </template>

          <template #head(Contre)>
            <span>Contre</span>
            <br />
            <b-button
              size="sm"
              class="text-nowrap"
              variant="light"
              :disabled="currentMandate.voted_by_correspondence == correspondenceTypes.by_app"
              @click="setAll(voteTypes.against)"
            >
              {{ $t("general.Tous") }}
              <font-awesome-icon
                icon="check-square"
                :class="{ 'color-green': isChecked(voteTypes.against) }"
              />
            </b-button>
          </template>

          <template #head(Abs)>
            <span>Abs</span>
            <br />
            <b-button
              size="sm"
              class="text-nowrap"
              variant="light"
              :disabled="currentMandate.voted_by_correspondence == correspondenceTypes.by_app"
              @click="setAll(voteTypes.abstain)"
            >
              {{ $t("general.Tous") }}
              <font-awesome-icon
                icon="check-square"
                :class="{ 'color-green': isChecked(voteTypes.abstain) }"
              />
            </b-button>
          </template>

          <template #head(nsp)>
            <span>Défaillant</span>
            <br />
            <b-button
              size="sm"
              class="text-nowrap"
              variant="light"
              :disabled="currentMandate.voted_by_correspondence == correspondenceTypes.by_app"
              @click="setAll(voteTypes.nsp)"
            >
              {{ $t("general.Tous") }}
              <font-awesome-icon
                icon="check-square"
                :class="{ 'color-green': isChecked(voteTypes.nsp) }"
              />
            </b-button>
          </template>

          <template #cell(index)="data">
            <div v-if="data.item.parent_resolution_id == null">
              <span>{{ data.item.position }}</span>
            </div>
            <div v-else>
              <span v-if="data.item.resolution_number">{{
                data.item.parent_resolution.resolution_number + "." + data.item.position
              }}</span>
              <span v-else>{{
                data.item.parent_resolution.position + "." + data.item.position
              }}</span>
            </div>
          </template>
          <template v-slot:cell(title)="data" class="resolution-name">
            <div v-if="data.item.children_resolutions.length == 0">
              <span class="cursor-pointer" @click="loadResolutionModal(data.item)">
                {{ data.item.name }}
              </span>
              <b-badge v-if="data.item.voting_category_id" class="ml-2" variant="warning">
                {{
                  getUsedMandateTantiemesByVotingCategory(mandate.id, data.item.voting_category_id)
                }}
              </b-badge>
              <b-badge
                v-if="
                  data.item.voting_category_id && data.item.majority_id != defaultSansVoteMajorityId
                "
                class="ml-2"
              >
                {{ getVotingCategoryName(data.item.voting_category_id) }}
              </b-badge>
              <b-badge v-if="data.item.has_revote" variant="primary" class="ml-2">
                {{ "Revote - " + data.item.majority.name }}
              </b-badge>
            </div>

            <div v-else class="cursor-pointer" @click="loadResolutionModal(data.item)">
              <span>
                {{ data.item.name }}
              </span>
            </div>
          </template>

          <template v-slot:cell(Pour)="data">
            <checkBoxComponent
              v-if="
                data.item.children_resolutions.length == 0 &&
                data.item.majority_id != defaultSansVoteMajorityId
              "
              :ref="'for-' + data.item.id"
              :key="data.item.key"
              :data="data"
              :type="voteTypes.for"
              :disabled="
                getTantiemes(data.item) == 0 ||
                currentMandate.voted_by_correspondence == correspondenceTypes.by_app
              "
              :meeting-correspondence-votes="meetingCorrespondenceVotes"
              @setProposedVote="setProposedVote"
            />
          </template>

          <template v-slot:cell(Contre)="data">
            <checkBoxComponent
              v-if="
                data.item.children_resolutions.length == 0 &&
                data.item.majority_id != defaultSansVoteMajorityId
              "
              :ref="'against-' + data.item.id"
              :key="data.item.key"
              :data="data"
              :type="voteTypes.against"
              :disabled="
                getTantiemes(data.item) == 0 ||
                currentMandate.voted_by_correspondence == correspondenceTypes.by_app
              "
              :meeting-correspondence-votes="meetingCorrespondenceVotes"
              @setProposedVote="setProposedVote"
            />
          </template>

          <template v-slot:cell(Abs)="data">
            <checkBoxComponent
              v-if="
                data.item.children_resolutions.length == 0 &&
                data.item.majority_id != defaultSansVoteMajorityId
              "
              :ref="'abstain-' + data.item.id"
              :key="data.item.key"
              :data="data"
              :type="voteTypes.abstain"
              :disabled="
                getTantiemes(data.item) == 0 ||
                currentMandate.voted_by_correspondence == correspondenceTypes.by_app
              "
              :meeting-correspondence-votes="meetingCorrespondenceVotes"
              @setProposedVote="setProposedVote"
            />
          </template>

          <template v-slot:cell(nsp)="data">
            <checkBoxComponent
              v-if="
                data.item.children_resolutions.length == 0 &&
                data.item.majority_id != defaultSansVoteMajorityId
              "
              :ref="'nsp-' + data.item.id"
              :key="data.item.key"
              :data="data"
              :type="voteTypes.nsp"
              :disabled="
                getTantiemes(data.item) == 0 ||
                currentMandate.voted_by_correspondence == correspondenceTypes.by_app
              "
              :meeting-correspondence-votes="meetingCorrespondenceVotes"
              @setProposedVote="setProposedVote"
            />
          </template>
        </b-table>
      </b-col>
      <b-modal id="resolution-modal" no-body hide-footer centered :title="modal.name">
        <b-row v-if="modal.parent_resolution" class="p-4 my-3">
          {{ modal.parent_resolution.name }}
        </b-row>
        <b-row v-if="modal.parent_resolution" class="p-4 my-3">
          {{ modal.parent_resolution.description }}
        </b-row>
        <b-row class="p-4 my-3">{{ modal.description }}</b-row>
      </b-modal>
    </b-row>
  </div>
</template>

<script>
import MeetingResolutionORM from "@/store/orm-axios/models/MeetingResolution";
import ResolutionVoteORM from "@/store/orm-axios/models/ResolutionVote";
import MandateORM from "@/store/orm-axios/models/Mandate";

import MandateMW from "@/middlewares/Mandate";

import minutesMixin from "@/mixins/minutes.js";
import { defaultSansVoteMajorityId } from "@/constants/default.js";
import MajorityORM from "@/store/orm-axios/models/Majority";
import MeetingCorrespondenceVoteORM from "@/store/orm-axios/models/MeetingCorrespondenceVote";
import MeetingCorrespondenceVoteMW from "@/middlewares/MeetingCorrespondenceVote";

export default {
  components: {
    checkBoxComponent: () => import("./VotingCheckboxCorrespondence"),
  },
  mixins: [minutesMixin],
  props: {
    currentMandateId: {
      type: String,
      required: true,
    },
    currentMandate: {
      type: Object,
      default: () => {
        return {};
      },
    },
    isFormInvalid: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      defaultSansVoteMajorityId,
      presence_status: null,
      isLoading: true,
      modal: {
        title: "",
        description: "",
      },
      fields: [
        {
          label: "Position",
          key: "index",
          class: "text-left position-column-width",
        },
        {
          label: "Resolution",
          key: "title",
          sortable: false,
          class: "text-left",
        },
        {
          label: this.$t("dt_voting_for_key"),
          key: "Pour",
          sortable: false,
        },
        {
          label: this.$t("dt_voting_against_key"),
          key: "Contre",
          sortable: false,
        },
        {
          label: this.$t("dt_voting_abstain_key"),
          key: "Abs",
          sortable: false,
        },
        {
          label: "Défaillant",
          key: "nsp",
          sortable: false,
        },
      ],
    };
  },
  computed: {
    meetingResolutions() {
      const meetingResolutions = this.getMeetingResolutions();
      return this.filterMeetingResolutions(meetingResolutions);
    },
    meetingResolutionsWithParents() {
      const meetingResolutions = this.getMeetingResolutions();
      return this.filterMeetingResolutionsWithParents(meetingResolutions);
    },
    mandate() {
      return MandateORM.query().where("id", this.currentMandateId).first();
    },
    majorities() {
      return MajorityORM.getters("getAll");
    },
    meetingCorrespondenceVotes() {
      return MeetingCorrespondenceVoteORM.query()
        .where("mandate_id", this.currentMandateId)
        .first();
    },
  },
  async created() {
    this.$store.state.showLoaderOverlay = true;
    await MandateMW.GET(this.currentMandateId)
      .then(async res => {
        var mandate = MandateORM.query().where("id", this.currentMandateId).first();
        this.$emit("set-last-mandate", mandate);
        if (
          mandate.presence_status != this.mandatePresenceStatuses.correspondence &&
          mandate.presence_status != this.mandatePresenceStatuses.invalid_correspondence
        ) {
          await MandateORM.update({
            where: mandate.id,
            data: {
              presence_status: this.mandatePresenceStatuses.correspondence,
              showed_up: 0,
            },
          });
        }

        if (
          this.mandate.meeting_correspondence_votes &&
          this.mandate.meeting_correspondence_votes.length > 0 &&
          this.mandate.meeting_correspondence_votes[0].id
        ) {
          await MeetingCorrespondenceVoteMW.GET(mandate.meeting_correspondence_votes[0].id).then(
            () => {
              if (
                this.meetingCorrespondenceVotes.proposed_votes == null ||
                this.meetingCorrespondenceVotes.proposed_votes.length == 0
              ) {
                this.setAll(null);
              }
            }
          );
        } else {
          await MeetingCorrespondenceVoteMW.POST({
            meeting_id: this.$route.params.id,
            mandate_id: mandate.id,
            agency_id: Number(localStorage.getItem("agencyId")),
          }).then(() => {
            this.setAll(null);
          });
        }

        this.isLoading = false;
        this.$store.state.showLoaderOverlay = false;
      })
      .catch(err => {
        this.isLoading = false;
        this.$store.state.showLoaderOverlay = false;
        console.error("MandateMW.GET err", err);
      });
  },
  beforeDestroy() {
    // HACK: Make sure to delete all unused resolution votes to prevent duplicates
    this.deleteUncreatedResolutionVotes();
  },
  methods: {
    isChecked(type) {
      var allVotes = ResolutionVoteORM.query()
        .where(rv => {
          return rv.mandate_id == this.currentMandateId;
        })
        .get();

      var votesWithThisType = ResolutionVoteORM.query()
        .where(rv => {
          return rv.mandate_id == this.currentMandateId && rv.vote == type;
        })
        .get();

      return allVotes.length == votesWithThisType.length && votesWithThisType.length > 0;
    },
    loadResolutionModal($ev) {
      this.$bvModal.show("resolution-modal");
      this.modal = $ev;
    },
    async setAll(type) {
      this.meetingResolutions.forEach(meetingResolution => {
        if (
          this.getTantiemes(meetingResolution) > 0 &&
          meetingResolution.majority_id != this.defaultSansVoteMajorityId
        ) {
          this.setOrUpdateProposedVotes(
            meetingResolution.id,
            meetingResolution.majority.id,
            type,
            true
          );
        }
      });
    },
    setProposedVote(event) {
      this.setOrUpdateProposedVotes(event.meetingResolutionId, event.majorityId, event.vote);
    },
    setOrUpdateProposedVotes(meetingResolutionId, majorityId, vote, voteAll = false) {
      let proposed_votes = this.meetingCorrespondenceVotes.proposed_votes;
      if (proposed_votes && proposed_votes.length > 0) {
        let existentVoteIndex = this.$_.findIndex(proposed_votes, item => {
          return (
            item.majority_id == majorityId && item.meeting_resolution_id == meetingResolutionId
          );
        });
        if (existentVoteIndex != "-1") {
          if (voteAll) {
            proposed_votes[existentVoteIndex].vote = vote;
          } else {
            // If same checkbox is selected, vote will be null (checkbox is unchecked)
            proposed_votes[existentVoteIndex].vote =
              proposed_votes[existentVoteIndex].vote == vote ? null : vote;
          }
        } else {
          proposed_votes.push({
            majority_id: majorityId,
            meeting_resolution_id: meetingResolutionId,
            vote: vote,
          });
        }
      } else {
        proposed_votes = [
          {
            majority_id: majorityId,
            meeting_resolution_id: meetingResolutionId,
            vote: vote,
          },
        ];
      }
      MeetingCorrespondenceVoteORM.update({
        where: this.meetingCorrespondenceVotes.id,
        data: {
          proposed_votes: proposed_votes,
        },
      });
    },
    getVotingCategoryName(votingCategoryId) {
      let votingCategory = this.findVotingCategoryData(votingCategoryId);
      return votingCategory ? votingCategory.name : "";
    },
    detailsClassFn(item, rowType) {
      let tantiemes = this.getUsedMandateTantiemesByVotingCategory(
        this.mandate.id,
        item.voting_category_id
      );
      if (item.children_resolutions.length > 0) {
        return "correspondence-votes-table-parent-row";
      } else if (item.$disabled) {
        return "bg-secondary not-editable-table";
      } else if (tantiemes == 0) {
        return "bg-warning-light not-editable";
      } else if (item.majority_id == this.defaultSansVoteMajorityId) {
        return "correspondence-sans-vote-majority-bg not-editable-table";
      } else if (item.has_revote) {
        return "bg-secondary-light";
      }
    },
    getTantiemes(item) {
      return this.getUsedMandateTantiemesByVotingCategory(this.mandate.id, item.voting_category_id);
    },
    deleteUncreatedResolutionVotes() {
      if (this.$_.isObject(this.currentMandate)) {
        var votes = ResolutionVoteORM.query().where("mandate_id", this.currentMandateId).get();
        for (const vote of votes) {
          if (vote && (vote.id.includes("$uid") || vote.created == null)) {
            ResolutionVoteORM.delete(vote.id);
          }
        }
      }
    },
    getMeetingResolutions() {
      return MeetingResolutionORM.query()
        .where("meeting_id", this.$route.params.id)
        .where("parent_resolution_id", null)
        .withAllRecursive(2)
        .with("children_resolutions", query => {
          query.withAllRecursive(1);
          query.with("resolution_votes", query2 => {
            query2.where(resolution_vote => {
              return resolution_vote.mandate_id == this.currentMandateId;
            });
          });
          query.orderBy("position");
        })
        .with("resolution_votes", query => {
          query.where(resolution_vote => {
            return resolution_vote.mandate_id == this.currentMandateId;
          });
        })
        .orderBy("position")
        .get();
    },
    filterMeetingResolutions(meetingResolutions) {
      let result = [];
      this.$_.each(meetingResolutions, mr => {
        if (mr.children_resolutions.length == 0 && mr.parent_resolution_id == null) {
          result.push(mr);
          let revoteMeetingResolution = this.getFirstRevoteMajority(mr);
          result = result.concat(revoteMeetingResolution);
        } else {
          this.$_.each(mr.children_resolutions, cmr => {
            result.push(cmr);
            let revoteMeetingResolution = this.getFirstRevoteMajority(cmr);
            result = result.concat(revoteMeetingResolution);
          });
        }
      });
      return result;
    },
    filterMeetingResolutionsWithParents(meetingResolutions) {
      let result = [];
      this.$_.each(meetingResolutions, mr => {
        if (mr.children_resolutions.length == 0 && mr.parent_resolution_id == null) {
          result.push(mr);
          let revoteMeetingResolution = this.getFirstRevoteMajority(mr);
          result = result.concat(revoteMeetingResolution);
        } else {
          result.push(mr);
          this.$_.each(mr.children_resolutions, cmr => {
            result.push(cmr);
            let revoteMeetingResolution = this.getFirstRevoteMajority(cmr);
            result = result.concat(revoteMeetingResolution);
          });
        }
      });
      return result;
    },
    getRevoteMajority(majorityId) {
      let majority = this.$_.find(this.majorities, item => {
        return item.id == majorityId;
      });
      if (majority.majority_conditions && majority.majority_conditions.revote) {
        return this.$_.find(this.majorities, item => {
          return item.id == majority.majority_conditions.revoteMajority;
        });
      }
      return null;
    },
    getFirstRevoteMajority(meetingResolution) {
      let revoteMeetingResolution = [];
      let newMeetingResolution = { ...meetingResolution };
      let revoteMajority = this.getRevoteMajority(newMeetingResolution.majority_id);
      if (revoteMajority != null) {
        newMeetingResolution.majority = revoteMajority;
        newMeetingResolution.majority_id = revoteMajority.id;
        newMeetingResolution.has_revote = true;
        revoteMeetingResolution.push(newMeetingResolution);
      }
      return revoteMeetingResolution;
    },
  },
};
</script>
